import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllEmployee, deleteCrewMember, editCrewMember } from "../../redux/actions/crewEmployee";
import { getAllZonesApi } from "../../apis/districts";
import { downloadCrewMembersApi } from "../../apis/crewEmployee";
import { EditOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Space, Tag, Popconfirm, message } from "antd";
import CustomModal from "../../common/CustomModal";
import EditCrewMember from "./EditCrewMember";
import ImageOption from "./ImageOption";
import CustomTable from "../../common/CustomTable";
import FilterCrewMember from "./FilterCrewMember";

function AllCrewMembers() {
  const dispatch = useDispatch();
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [filters, setFilters] = useState({});
  const [zones, setZones] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";

  const { object: crewMembers = [], totalCount = 0, isProcessing: isLoading } =
    useSelector((state) => state.crewEmployee.data || {});

  useEffect(() => {
    setPagination((prev) => ({ ...prev, total: totalCount }));
  }, [totalCount]);

  useEffect(() => {
    if (isSuperAdmin) getAllZonesApi().then((res) => { if (res) setZones(res); });
  }, []);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const tenantId = currentUser?.tenant?.id || null;
    const params = tenantId
      ? { page: pagination.page - 1, size: pagination.pageSize, ...filters, tenantId }
      : { page: pagination.page - 1, size: pagination.pageSize, ...filters, zoneId: currentUser?.zone?.id || null, zoneName: currentUser?.zone?.name || null };
    dispatch(getAllEmployee(params));
  }, [dispatch, pagination.page, pagination.pageSize, filters]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size) => {
    setPagination({ page: 1, pageSize: size, total: pagination.total });
  };

  const handleDelete = (id) => {
    dispatch(deleteCrewMember(id))
  };

  const handleSubmit = (payload) => {
    dispatch(editCrewMember(payload));
  };

  const handleEdit = (record) => {
    setSelectedMember(record);
    setModalVisible(true);
  };

  const handleDownload = () => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const tenantId = currentUser?.tenant?.id || null;
    const locationParams = tenantId
      ? { tenantId }
      : { zoneId: currentUser?.zone?.id || null, zoneName: currentUser?.zone?.name || null };
    downloadCrewMembersApi({ ...filters, ...locationParams })
      .then((res) => {
        const blob = new Blob([res], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "crew_members.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error("Failed to download crew members."));
  };

  const refreshList = useCallback(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const tenantId = currentUser?.tenant?.id || null;
    const params = tenantId
      ? { page: pagination.page - 1, size: pagination.pageSize, ...filters, tenantId }
      : { page: pagination.page - 1, size: pagination.pageSize, ...filters, zoneId: currentUser?.zone?.id || null, zoneName: currentUser?.zone?.name || null };
    dispatch(getAllEmployee(params));
  }, [dispatch, pagination.page, pagination.pageSize, filters]);

  const columns = useMemo(() => [
    { title: "ID", dataIndex: "employeeId", key: "employeeId" },
    { title: "Code", dataIndex: "employeeCode", key: "employeeCode" },
    { title: "Name", dataIndex: "employeeName", key: "employeeName" },
    {
      title: "Image",
      key: "image",
      render: (_, record) => <ImageOption record={record} onImageUpdated={refreshList} />,
    },
    { title: "Father's Name", dataIndex: "employeeFatherName", key: "employeeFatherName" },
    { title: "Mobile", dataIndex: "mobileNumber", key: "mobileNumber" },
    {
      title: "Latitude",
      dataIndex: "locations",
      key: "latitude",
      render: (locations, record) =>
        Array.isArray(locations)
          ? locations.map((location) => location.latitude).join(", ")
          : record.latitude,
    },
    {
      title: "Longitude",
      dataIndex: "locations",
      key: "longitude",
      render: (locations, record) =>
        Array.isArray(locations)
          ? locations.map((location) => location.longitude).join(", ")
          : record.longitude,
    },
    {
      title: "Photo Change Restriction",
      key: "photoChangeRestricted",
      align: 'center',
      render: (_, record) => (
        <Tag color={record.photoChangeRestricted ? "blue" : "volcano"}>
          {record.photoChangeRestricted ? "Restricted" : "Allowed"}
        </Tag>
      ),
    },
    {
      title: "Geo-Fencing",
      key: "geofencingOn",
      align: 'center',
      render: (_, record) => (
        <Tag color={record.geofencingOn ? "green" : "red"}>
          {record.geofencingOn ? "Enabled" : "Disabled"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>

          <Popconfirm
            title="Are you sure you want to delete this crew member?"
            onConfirm={() => handleDelete(record.employeeId)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    }
  ], [refreshList]);


  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1>All Employees List</h1>
        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
          Download Excel
        </Button>
      </div>
      <FilterCrewMember
        onFilter={(filters) => {
          setPagination((prev) => ({ ...prev, page: 1 }));
          setFilters(filters);
        }}
      />
      {isLoading && <p>Loading...</p>}
      {crewMembers?.length > 0 ? (
        <CustomTable
          columns={columns}
          data={crewMembers}
          loading={isLoading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      ) : (
        <p>No Employees Found</p>
      )}
      {isModalVisible && (
        <CustomModal
          visible={isModalVisible}
          onClose={() => setModalVisible(false)}
          title="Edit Crew Member"
          content={
            <EditCrewMember
              member={selectedMember}
              onClose={() => setModalVisible(false)}
              onSubmit={handleSubmit}
              zones={zones}
              isSuperAdmin={isSuperAdmin}
            />
          }
        />
      )}
    </div>
  );
}

export default AllCrewMembers;
